import React from 'react';
import { Pressable } from 'react-native';

import { Scroll, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { auth } from '@/features/auth/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import { NOTIFICATION_PREFERENCE_ITEMS } from './constants';
import { NotificationPermissionBanner } from './notification-permission-banner';
import { NotificationPreferenceRow } from './notification-preference-row';
import type { NotificationPreferenceKey } from './types';

const strings = STRINGS.guardianProfile.notificationPreferences;

type NotificationPreferencesContentProps = {
  tabletPanel?: boolean;
  notificationCenterTablet?: boolean;
  preferences: auth.NotificationPreferences;
  showPermissionBanner: boolean;
  permissionBannerMessage: string;
  isDirty: boolean;
  saving: boolean;
  onSave: () => void;
  onPermissionBannerPress?: () => void;
  onPreferenceChange: (key: NotificationPreferenceKey, value: boolean) => void;
};

export function NotificationPreferencesContent({
  tabletPanel = false,
  notificationCenterTablet = false,
  preferences,
  showPermissionBanner,
  permissionBannerMessage,
  isDirty,
  saving,
  onSave,
  onPermissionBannerPress,
  onPreferenceChange,
}: NotificationPreferencesContentProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const saveDisabled = !isDirty || saving;

  const saveAction = (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: saveDisabled }}
      accessibilityLabel={strings.save}
      disabled={saveDisabled}
      onPress={onSave}
      style={({ pressed }) => [
        styles.tabletSaveButton,
        isDirty ? styles.tabletSaveButtonEnabled : null,
        pressed && !saveDisabled ? styles.pressed : null,
      ]}>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
        style={isDirty ? styles.tabletSaveTextEnabled : styles.tabletSaveText}>
        {strings.save}
      </Text>
    </Pressable>
  );

  return (
    <Scroll
      style={styles.scroll}
      contentContainerStyle={[
        styles.content,
        notificationCenterTablet ? styles.notificationCenterTabletContent : null,
      ]}
      backgroundColor={notificationCenterTablet ? 'transparent' : guardianColors['1']}
      showsVerticalScrollIndicator={false}>
      {tabletPanel ? (
        <Stack
          direction="row"
          align="center"
          justify="space-between"
          style={styles.tabletHeadingRow}>
          <Text variant="Font Guardian App/Heading/Heading 24" style={styles.title}>
            {strings.tabletTitle}
          </Text>
          {saveAction}
        </Stack>
      ) : null}

      <Text
        variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
        style={[styles.subtitle, tabletPanel ? styles.subtitleTablet : null]}>
        {strings.subtitle}
      </Text>

      {showPermissionBanner ? (
        <NotificationPermissionBanner
          message={permissionBannerMessage}
          onPress={onPermissionBannerPress}
          style={styles.warningSpacing}
        />
      ) : null}

      <Stack style={[styles.card, tabletPanel ? styles.cardTablet : null]} gap={scale(8)}>
        {NOTIFICATION_PREFERENCE_ITEMS.map(item => (
          <NotificationPreferenceRow
            key={item.key}
            icon={item.icon}
            title={item.title}
            subtitle={item.subtitle}
            value={preferences[item.key]}
            onValueChange={value => onPreferenceChange(item.key, value)}
          />
        ))}
      </Stack>
    </Scroll>
  );
}

export * from './constants';
export * from './types';

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: scale({ phone: 16, tablet: 20 }),
    paddingTop: scale({ phone: 12, tablet: 16 }),
  },
  notificationCenterTabletContent: {
    width: scale(380),
    alignSelf: 'center',
    paddingHorizontal: 0,
    paddingTop: scale(16),
  },
  phonePanel: {
    paddingTop: scale(16),
    paddingHorizontal: scale(16),
  },
  tabletPanel: {
    paddingTop: scale(16),
    paddingHorizontal: scale(20),
  },
  tabletHeadingRow: {
    height: scale(48),
  },
  title: {
    color: guardianColors['10'],
  },
  tabletSaveButton: {
    width: scale(100),
    height: scale(48),
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['2'],
    shadowColor: '#292757',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 1,
  },
  tabletSaveButtonEnabled: {
    backgroundColor: guardianColors['Main/Main'],
  },
  tabletSaveText: {
    color: guardianColors['4'],
  },
  tabletSaveTextEnabled: {
    color: colors['0'],
  },
  subtitle: {
    color: guardianColors['6'],
  },
  subtitleTablet: {
    marginTop: scale(8),
  },
  warningSpacing: {
    marginTop: scale({ phone: 12, tablet: 20 }),
  },
  card: {
    marginTop: scale({ phone: 12, tablet: 20 }),
    borderRadius: scale(16),
    backgroundColor: colors['0'],
    overflow: 'hidden',
  },
  cardTablet: {
    borderRadius: scale(12),
  },
  pressed: {
    opacity: 0.72,
  },
}));
